import { ChevronRightIcon } from "@partners/ui";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "../Button";
import { Typo } from "../Typo";

export type MobileDateRange = {
  from: Date | null;
  to: Date | null;
};

type MobileDateRangePickerProps = {
  value: MobileDateRange;
  onChange: (value: MobileDateRange) => void;
  minDate: Date;
  maxDate: Date;
  limitDays?: number;
  initialViewDate?: Date;
  onViewChange?: (view: "calendar" | "picker") => void;
  /** Label for the Confirm button shown in picker mode */
  confirmLabel?: string;
};

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const DAY_IN_MS = 24 * 60 * 60 * 1000;
const ITEM_HEIGHT = 44;
const VISIBLE_ITEMS = 5;
const PADDING_ITEMS = Math.floor(VISIBLE_ITEMS / 2); // 2

// ─── Date helpers ──────────────────────────────────────────────

const startOfDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const isBeforeDay = (a: Date, b: Date) =>
  startOfDay(a).getTime() < startOfDay(b).getTime();

const isAfterDay = (a: Date, b: Date) =>
  startOfDay(a).getTime() > startOfDay(b).getTime();

const isBetweenDays = (date: Date, from: Date, to: Date) => {
  const d = startOfDay(date).getTime();
  return d > startOfDay(from).getTime() && d < startOfDay(to).getTime();
};

const formatDate = (date: Date) => {
  const day = date.getDate();
  const month = date.toLocaleString("en-US", { month: "short" });
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

// ─── Wheel Column ──────────────────────────────────────────────

type WheelItem = { label: string; value: number };

type WheelColumnProps = {
  items: WheelItem[];
  selectedValue: number;
  onChange: (value: number) => void;
};

const WheelColumn = ({ items, selectedValue, onChange }: WheelColumnProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const selectedIdx = items.findIndex((i) => i.value === selectedValue);

  // Instantly jump to the correct position on first render (before paint)
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el || selectedIdx < 0) return;
    el.scrollTop = selectedIdx * ITEM_HEIGHT;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Smooth-scroll whenever the selected value changes after mount
  useEffect(() => {
    const el = containerRef.current;
    if (!el || selectedIdx < 0) return;
    el.scrollTo({ top: selectedIdx * ITEM_HEIGHT, behavior: "smooth" });
  }, [selectedIdx]);

  useEffect(() => {
    return () => {
      if (scrollTimerRef.current) {
        clearTimeout(scrollTimerRef.current);
        scrollTimerRef.current = null;
      }
    };
  }, []);

  const handleScroll = () => {
    if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
    scrollTimerRef.current = setTimeout(() => {
      const el = containerRef.current;
      if (!el) return;
      const idx = Math.round(el.scrollTop / ITEM_HEIGHT);
      const clamped = Math.max(0, Math.min(idx, items.length - 1));
      const item = items[clamped];
      if (item && item.value !== selectedValue) {
        onChange(item.value);
        el.scrollTo({ top: clamped * ITEM_HEIGHT, behavior: "smooth" });
      }
    }, 120);
  };

  return (
    <div
      className="relative flex-1 overflow-hidden"
      style={{ height: ITEM_HEIGHT * VISIBLE_ITEMS }}
    >
      {/* Selected row highlight — #03142E at 10% opacity */}
      <div
        className="pointer-events-none absolute inset-x-1 z-0 rounded-lg bg-overlay-opacity-state-pressed"
        style={{ top: PADDING_ITEMS * ITEM_HEIGHT, height: ITEM_HEIGHT }}
      />
      {/* Top fade */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 z-10 bg-gradient-to-b from-surface-primary to-transparent"
        style={{ height: PADDING_ITEMS * ITEM_HEIGHT }}
      />
      {/* Bottom fade */}
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-surface-primary to-transparent"
        style={{ height: PADDING_ITEMS * ITEM_HEIGHT }}
      />

      <div
        ref={containerRef}
        className="h-full overflow-y-scroll"
        style={{
          scrollSnapType: "y mandatory",
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
        onScroll={handleScroll}
      >
        {/* Top padding so the first real item can scroll to centre */}
        {Array.from({ length: PADDING_ITEMS }).map((_, i) => (
          <div
            key={`top-${i}`}
            style={{ height: ITEM_HEIGHT, scrollSnapAlign: "center" }}
          />
        ))}

        {items.map((item) => (
          <div
            key={item.value}
            className={`typo-label-sm flex items-center justify-center ${
              item.value === selectedValue
                ? "text-text-primary"
                : "text-text-tertiary"
            }`}
            style={{ height: ITEM_HEIGHT, scrollSnapAlign: "center" }}
          >
            {item.label}
          </div>
        ))}

        {/* Bottom padding */}
        {Array.from({ length: PADDING_ITEMS }).map((_, i) => (
          <div
            key={`bot-${i}`}
            style={{ height: ITEM_HEIGHT, scrollSnapAlign: "center" }}
          />
        ))}
      </div>
    </div>
  );
};

// ─── Main Picker ───────────────────────────────────────────────

export const MobileDateRangePicker = ({
  value,
  onChange,
  minDate,
  maxDate,
  limitDays,
  initialViewDate,
  onViewChange,
  confirmLabel = "Confirm",
}: MobileDateRangePickerProps) => {
  const { t } = useTranslation();
  const today = startOfDay(new Date());

  const [viewDate, setViewDate] = useState<Date>(() => {
    const anchor = initialViewDate ?? today;
    return new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  });
  const [view, setView] = useState<"calendar" | "picker">("calendar");

  // Picker columns track their own state while the picker is open;
  // changes are committed back to viewDate only when Confirm is tapped.
  const [pickerYear, setPickerYear] = useState(viewDate.getFullYear());
  const [pickerMonth, setPickerMonth] = useState(viewDate.getMonth());

  const viewYear = viewDate.getFullYear();
  const viewMonth = viewDate.getMonth();

  const switchView = (next: "calendar" | "picker") => {
    setView(next);
    onViewChange?.(next);
  };

  // ── Calendar grid ──────────────────────────────────────────────

  const gridCells = (() => {
    const firstWeekday = new Date(viewYear, viewMonth, 1).getDay();
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const prevMonthDays = new Date(viewYear, viewMonth, 0).getDate();

    type Cell =
      | { type: "prev"; date: Date }
      | { type: "current"; date: Date }
      | { type: "next"; date: Date };

    const cells: Cell[] = [];

    // Leading overflow — previous month's trailing days, faded
    for (let i = firstWeekday - 1; i >= 0; i--) {
      cells.push({
        type: "prev",
        date: new Date(viewYear, viewMonth - 1, prevMonthDays - i),
      });
    }

    // Current month days
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push({ type: "current", date: new Date(viewYear, viewMonth, d) });
    }

    // Trailing overflow — next month's leading days, faded
    let nextDay = 1;
    while (cells.length % 7 !== 0) {
      cells.push({
        type: "next",
        date: new Date(viewYear, viewMonth + 1, nextDay++),
      });
    }

    return cells;
  })();

  // ── Month navigation ───────────────────────────────────────────

  const canGoPrevMonth =
    new Date(viewYear, viewMonth - 1, 1) >=
    new Date(minDate.getFullYear(), minDate.getMonth(), 1);

  const canGoNextMonth =
    new Date(viewYear, viewMonth + 1, 1) <=
    new Date(maxDate.getFullYear(), maxDate.getMonth(), 1);

  const handlePrevMonth = () => {
    if (canGoPrevMonth) setViewDate(new Date(viewYear, viewMonth - 1, 1));
  };

  const handleNextMonth = () => {
    if (canGoNextMonth) setViewDate(new Date(viewYear, viewMonth + 1, 1));
  };

  // ── Wheel picker ───────────────────────────────────────────────

  const openPicker = () => {
    setPickerYear(viewYear);
    setPickerMonth(viewMonth);
    switchView("picker");
  };

  const confirmPicker = () => {
    setViewDate(new Date(pickerYear, pickerMonth, 1));
    switchView("calendar");
  };

  const yearItems: WheelItem[] = [];
  for (let y = minDate.getFullYear(); y <= maxDate.getFullYear(); y++) {
    yearItems.push({ label: String(y), value: y });
  }

  const monthItems = (() => {
    const min = pickerYear === minDate.getFullYear() ? minDate.getMonth() : 0;
    const max = pickerYear === maxDate.getFullYear() ? maxDate.getMonth() : 11;
    const items: WheelItem[] = [];
    for (let m = min; m <= max; m++) {
      items.push({ label: MONTH_NAMES[m], value: m });
    }
    return items;
  })();

  const handlePickerYearChange = (year: number) => {
    setPickerYear(year);
    // Clamp selected month to the valid range for the new year
    const maxM = year === maxDate.getFullYear() ? maxDate.getMonth() : 11;
    const minM = year === minDate.getFullYear() ? minDate.getMonth() : 0;
    if (pickerMonth > maxM) setPickerMonth(maxM);
    else if (pickerMonth < minM) setPickerMonth(minM);
  };

  // ── Day tap ────────────────────────────────────────────────────

  const isOutsideLimit = (date: Date) => {
    if (!limitDays || !value.from || value.to) return false;
    const days = Math.round(
      (startOfDay(date).getTime() - startOfDay(value.from).getTime()) /
        DAY_IN_MS,
    );
    return Math.abs(days) > limitDays;
  };

  const isDateDisabled = (date: Date) =>
    isBeforeDay(date, minDate) ||
    isAfterDay(date, maxDate) ||
    isOutsideLimit(date);

  const handleDayTap = (date: Date) => {
    if (isDateDisabled(date)) return;

    const { from, to } = value;

    // Fresh selection: nothing selected, or both already set
    if (!from || (from && to)) {
      onChange({ from: date, to: null });
      return;
    }

    // Waiting for end date
    if (isBeforeDay(date, from)) {
      // Tapping before start → move start to new tap, clear end
      onChange({ from: date, to: null });
      return;
    }

    // date >= from → set end (same day = single-day range)
    onChange({ from, to: date });
  };

  // ── Cell state ─────────────────────────────────────────────────

  type CellState =
    | "start"
    | "end"
    | "inRange"
    | "today"
    | "normal"
    | "disabled";

  const getCellState = (date: Date): CellState => {
    if (isDateDisabled(date)) return "disabled";
    const { from, to } = value;
    if (from && isSameDay(date, from)) return "start";
    if (to && isSameDay(date, to)) return "end";
    if (from && to && isBetweenDays(date, from, to)) return "inRange";
    if (isSameDay(date, today)) return "today";
    return "normal";
  };

  const cellBaseClass =
    "flex h-10 w-full items-center justify-center typo-body-sm transition-colors";

  const cellClassFor = (state: CellState) => {
    switch (state) {
      case "start":
      case "end":
        return `${cellBaseClass} rounded-lg bg-surface-brand-default typo-label-sm text-text-inverse-primary`;
      case "inRange":
        return `${cellBaseClass} bg-surface-brand-subtle text-text-primary`;
      case "today":
        return `${cellBaseClass} rounded-lg border-2 border-surface-brand-default text-text-primary`;
      case "disabled":
        return `${cellBaseClass} text-text-tertiary opacity-40`;
      default:
        return `${cellBaseClass} text-text-primary active:bg-surface-brand-subtle active:rounded-lg`;
    }
  };

  // ── Active card logic ──────────────────────────────────────────

  // Start card is active when nothing is selected yet
  const startCardActive = !value.from;
  // End card is active when start is set but end is still empty
  const endCardActive = Boolean(value.from) && !value.to;

  const activeCardClass =
    "flex flex-1 flex-col gap-0.5 rounded-lg border-2 border-surface-brand-default bg-surface-brand-subtle px-3 py-2";
  const passiveCardClass =
    "flex flex-1 flex-col gap-0.5 rounded-lg bg-surface-secondary px-3 py-2";

  // ── Render ─────────────────────────────────────────────────────

  return (
    <div className="flex flex-col gap-2">
      {/* Month/year header — two modes */}
      <div className="flex items-center justify-between">
        {view === "calendar" ? (
          <>
            <button
              type="button"
              onClick={handlePrevMonth}
              disabled={!canGoPrevMonth}
              aria-label="Previous month"
              className="flex h-8 w-8 items-center justify-center rounded-lg disabled:opacity-30"
            >
              <ChevronRightIcon className="h-4 w-4 rotate-180 text-icon-primary" />
            </button>

            <button
              type="button"
              onClick={openPicker}
              className="flex items-center gap-1.5 rounded-lg px-2 py-1"
              aria-label="Open month and year picker"
            >
              <Typo type="labelSM" textColor="text-text-primary">
                {MONTH_NAMES[viewMonth]} {viewYear}
              </Typo>
              {/* Down chevron */}
              <ChevronRightIcon className="h-3 w-3 rotate-90 text-icon-primary" />
            </button>

            <button
              type="button"
              onClick={handleNextMonth}
              disabled={!canGoNextMonth}
              aria-label="Next month"
              className="flex h-8 w-8 items-center justify-center rounded-lg disabled:opacity-30"
            >
              <ChevronRightIcon className="h-4 w-4 text-icon-primary" />
            </button>
          </>
        ) : (
          /* Picker mode: centred label only, no side arrows */
          <button
            type="button"
            onClick={confirmPicker}
            className="flex flex-1 items-center justify-center gap-1.5 rounded-lg px-2 py-1"
            aria-label="Close month and year picker"
          >
            <Typo type="labelSM" textColor="text-text-primary">
              {MONTH_NAMES[pickerMonth]} {pickerYear}
            </Typo>
            {/* Up chevron */}
            <ChevronRightIcon className="h-3 w-3 -rotate-90 text-icon-primary" />
          </button>
        )}
      </div>

      {view === "calendar" ? (
        <>
          {/* Weekday header — Sun → Sat */}
          <div className="grid grid-cols-7">
            {DAY_NAMES.map((name) => (
              <div key={name} className="flex h-8 items-center justify-center">
                <Typo type="bodyXS" textColor="text-text-tertiary">
                  {name}
                </Typo>
              </div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="grid grid-cols-7">
            {gridCells.map((cell) => {
              // Overflow dates from previous or next month — faded, not tappable
              if (cell.type === "prev" || cell.type === "next") {
                return (
                  <div
                    key={`${cell.type}-${cell.date.toISOString()}`}
                    className="typo-body-sm flex h-10 w-full items-center justify-center text-text-tertiary opacity-30"
                  >
                    {cell.date.getDate()}
                  </div>
                );
              }

              const state = getCellState(cell.date);
              return (
                <button
                  key={cell.date.toISOString()}
                  type="button"
                  onClick={() => handleDayTap(cell.date)}
                  disabled={state === "disabled"}
                  className={cellClassFor(state)}
                >
                  {cell.date.getDate()}
                </button>
              );
            })}
          </div>

          {/* Start / End summary cards */}
          <div className="flex items-center gap-2 border-t border-stroke-primary pt-4">
            <div
              className={startCardActive ? activeCardClass : passiveCardClass}
            >
              <Typo type="bodyXS" textColor="text-text-tertiary">
                {t("clientsPage_filters_Bottomsheet_start")}
              </Typo>
              <Typo
                type="labelSM"
                textColor={
                  value.from ? "text-text-primary" : "text-text-tertiary"
                }
              >
                {value.from ? formatDate(value.from) : "–"}
              </Typo>
            </div>

            <span className="text-text-tertiary">→</span>

            <div className={endCardActive ? activeCardClass : passiveCardClass}>
              <Typo type="bodyXS" textColor="text-text-tertiary">
                {t("clientsPage_filters_Bottomsheet_end")}
              </Typo>
              <Typo
                type="labelSM"
                textColor={
                  value.to ? "text-text-primary" : "text-text-tertiary"
                }
              >
                {value.to ? formatDate(value.to) : "–"}
              </Typo>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Wheel picker — two columns */}
          <div className="flex gap-2 py-2">
            <WheelColumn
              items={monthItems}
              selectedValue={pickerMonth}
              onChange={(v) => setPickerMonth(v as number)}
            />
            <WheelColumn
              items={yearItems}
              selectedValue={pickerYear}
              onChange={(v) => handlePickerYearChange(v as number)}
            />
          </div>

          {/* Confirm button — replaces footer in picker mode */}
          <Button
            variant="brand"
            size="md"
            className="w-full"
            onClick={confirmPicker}
          >
            {confirmLabel}
          </Button>
        </>
      )}
    </div>
  );
};
